/**
 * Health360 — School clinical operations module.
 * Executive dashboard + student profiles + visits + medications +
 * immunisations + incidents. All KPIs source the Central Metrics Service.
 */
import React, { useEffect, useMemo, useState } from "react";
import client, { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, KPITile, Section, Loading, StatusPill } from "../components/Common";
import { toast } from "sonner";
import {
  HeartPulse, Activity, Pill, Syringe, AlertTriangle, ClipboardList,
  Stethoscope, Shield, Plus, ChevronRight, Mail, X, Loader2,
} from "lucide-react";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: HeartPulse },
  { id: "visits", label: "Clinic Visits", icon: Stethoscope },
  { id: "medications", label: "Medications", icon: Pill },
  { id: "allergies", label: "Allergies", icon: AlertTriangle },
  { id: "immunisations", label: "Immunisations", icon: Syringe },
  { id: "incidents", label: "Incidents", icon: Shield },
];

export default function Health360() {
  const { tenant } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [modal, setModal] = useState(null);

  const loadDashboard = () => client.get("/health360/dashboard").then(({ data }) => setDashboard(data));
  const loadTab = async (t) => {
    const endpoint = { visits: "/health360/visits", medications: "/health360/medications", allergies: "/health360/allergies", immunisations: "/health360/immunisations", incidents: "/health360/incidents" }[t];
    if (!endpoint) return;
    const { data } = await client.get(endpoint);
    setItems(data.items || []);
  };
  useEffect(() => { loadDashboard(); api.students({ limit: 300 }).then(({ data }) => setStudents(data || [])); }, []);
  useEffect(() => { if (tab !== "dashboard") loadTab(tab); }, [tab]);

  if (!dashboard) return <Loading label="Loading Health360…" />;
  const k = dashboard.kpis;
  const studentById = Object.fromEntries((students || []).map((s) => [s.id, s]));

  return (
    <div data-testid="health360-page">
      <PageHeader
        kicker={`${tenant?.name} · Health360™`}
        title="Clinical Operations & Wellbeing"
        subtitle="Medical profiles, medications, allergies, immunisations, incidents and boarding administration — feeding the Executive Intelligence Centre."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="health-kpis">
        <KPITile testId="h-visits" tone="dark" label="Visits (30d)" value={k.visits_30d} sub={`${k.visits_total} all-time`} />
        <KPITile testId="h-severe" label="Severe allergies" value={k.severe_allergies} />
        <KPITile testId="h-meds" label="Active meds" value={k.active_medications} />
        <KPITile testId="h-immunisations" tone="dark" label="Immunisations" value={k.immunisation_records} sub={`${k.immunisations_due_30d} due 30d`} />
        <KPITile testId="h-chronic" label="Chronic cases" value={k.chronic_conditions} />
        <KPITile testId="h-coverage" label="Profile coverage" value={`${k.profile_coverage_pct}%`} sub={`${k.students_with_profile} profiles`} />
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="health-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-3.5 py-2 text-sm rounded-md border transition ${tab === t.id ? "bg-[color:var(--hl-navy)] text-white border-[color:var(--hl-navy)] shadow-sm" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Section title="Recent clinic visits" subtitle="Last 30 recorded visits">
            <div className="hl-card overflow-hidden">
              <table className="hl-table w-full text-sm" data-testid="recent-visits">
                <thead><tr><th>When</th><th>Student</th><th>Reason</th><th>Outcome</th></tr></thead>
                <tbody>
                  {(dashboard.recent_visits || []).slice(0, 12).map((v) => {
                    const s = studentById[v.student_id];
                    return (
                      <tr key={v.id}>
                        <td className="text-xs mono">{(v.visited_at || "").slice(0, 10)}</td>
                        <td>{s ? `${s.first_name} ${s.last_name}` : v.student_id.slice(0, 6)}</td>
                        <td className="text-xs">{v.reason}</td>
                        <td><span className="text-xs uppercase tracking-widest text-[color:var(--hl-orange)] font-semibold">{v.outcome}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
          <Section title="High-severity allergies" subtitle="Students with severe or life-threatening allergies">
            <div className="hl-card overflow-hidden">
              <table className="hl-table w-full text-sm" data-testid="severe-allergies-table">
                <thead><tr><th>Student</th><th>Substance</th><th>Reaction</th><th>Severity</th></tr></thead>
                <tbody>
                  {(dashboard.high_severity_allergies || []).map((a) => {
                    const s = studentById[a.student_id];
                    return (
                      <tr key={a.id}>
                        <td>{s ? `${s.first_name} ${s.last_name}` : a.student_id.slice(0, 6)}</td>
                        <td className="font-semibold">{a.substance}</td>
                        <td className="text-xs">{a.reaction}</td>
                        <td><span className={`text-xs font-semibold ${a.severity === "life_threatening" ? "text-red-600" : "text-amber-600"}`}>{a.severity}</span></td>
                      </tr>
                    );
                  })}
                  {!dashboard.high_severity_allergies?.length && (
                    <tr><td colSpan={4} className="text-center text-slate-400 py-6">No high-severity allergies flagged</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Section>
        </div>
      )}

      {tab !== "dashboard" && (
        <Section
          title={TABS.find((t) => t.id === tab).label}
          actions={<button onClick={() => setModal(tab)} data-testid={`btn-add-${tab}`} className="hl-btn-accent inline-flex items-center gap-1.5 text-sm px-3 py-2"><Plus className="w-4 h-4" />Record</button>}
        >
          <ItemsTable items={items} tab={tab} studentById={studentById} />
        </Section>
      )}

      {modal && (
        <RecordForm
          kind={modal}
          students={students}
          onClose={() => setModal(null)}
          onSaved={() => { loadTab(modal); loadDashboard(); setModal(null); }}
        />
      )}
    </div>
  );
}

function ItemsTable({ items, tab, studentById }) {
  const cols = {
    visits: [
      ["When", (v) => (v.visited_at || "").slice(0, 10)],
      ["Student", (v) => { const s = studentById[v.student_id]; return s ? `${s.first_name} ${s.last_name}` : v.student_id.slice(0, 6); }],
      ["Reason", (v) => v.reason],
      ["Treatment", (v) => v.treatment_given],
      ["Outcome", (v) => v.outcome],
      ["Notified", (v) => v.parent_notified ? "✓" : ""],
    ],
    medications: [
      ["Student", (m) => { const s = studentById[m.student_id]; return s ? `${s.first_name} ${s.last_name}` : m.student_id.slice(0, 6); }],
      ["Medication", (m) => m.name],
      ["Dosage", (m) => m.dosage],
      ["Schedule", (m) => m.schedule],
      ["Boarding", (m) => m.boarding_kept ? "✓" : ""],
      ["Active", (m) => m.active ? "Yes" : "No"],
    ],
    allergies: [
      ["Student", (a) => { const s = studentById[a.student_id]; return s ? `${s.first_name} ${s.last_name}` : a.student_id.slice(0, 6); }],
      ["Substance", (a) => a.substance],
      ["Reaction", (a) => a.reaction],
      ["Severity", (a) => a.severity],
    ],
    immunisations: [
      ["Student", (i) => { const s = studentById[i.student_id]; return s ? `${s.first_name} ${s.last_name}` : i.student_id.slice(0, 6); }],
      ["Vaccine", (i) => i.vaccine],
      ["Administered", (i) => i.administered_on],
      ["Next due", (i) => i.next_due],
      ["Batch", (i) => i.batch_no],
    ],
    incidents: [
      ["When", (i) => (i.occurred_at || "").slice(0, 10)],
      ["Student", (i) => { const s = studentById[i.student_id]; return s ? `${s.first_name} ${s.last_name}` : i.student_id.slice(0, 6); }],
      ["Location", (i) => i.location],
      ["Severity", (i) => i.severity],
      ["Description", (i) => i.description],
      ["Notified", (i) => i.parent_notified ? "✓" : ""],
    ],
  }[tab] || [];
  return (
    <div className="hl-card overflow-hidden">
      <table className="hl-table w-full text-sm" data-testid={`items-${tab}`}>
        <thead><tr>{cols.map((c) => <th key={c[0]}>{c[0]}</th>)}</tr></thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id}>{cols.map((c) => <td key={c[0]} className="text-xs">{c[1](it)}</td>)}</tr>
          ))}
          {!items.length && <tr><td colSpan={cols.length} className="text-center text-slate-400 py-6">No records</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function RecordForm({ kind, students, onClose, onSaved }) {
  const [form, setForm] = useState({ student_id: students[0]?.id || "" });
  const [saving, setSaving] = useState(false);
  const endpoints = {
    visits: "/health360/visits",
    medications: "/health360/medications",
    allergies: "/health360/allergies",
    immunisations: "/health360/immunisations",
    incidents: "/health360/incidents",
  };
  const submit = async () => {
    setSaving(true);
    try {
      await client.post(endpoints[kind], form);
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Unable to save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="health-form-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg capitalize">Record {kind.slice(0, -1)}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-slate-600">Student</span>
            <select value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded" data-testid="input-student">
              {students.slice(0, 300).map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select>
          </label>
          {kind === "visits" && <>
            <Field label="Reason" value={form.reason} onChange={(v) => setForm({ ...form, reason: v })} />
            <Field label="Treatment given" value={form.treatment_given} onChange={(v) => setForm({ ...form, treatment_given: v })} />
            <Field label="Outcome" value={form.outcome} onChange={(v) => setForm({ ...form, outcome: v })} placeholder="resolved / sent_home / referred" />
          </>}
          {kind === "medications" && <>
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="Dosage" value={form.dosage} onChange={(v) => setForm({ ...form, dosage: v })} />
            <Field label="Schedule" value={form.schedule} onChange={(v) => setForm({ ...form, schedule: v })} placeholder="OD / BID / PRN" />
          </>}
          {kind === "allergies" && <>
            <Field label="Substance" value={form.substance} onChange={(v) => setForm({ ...form, substance: v })} />
            <Field label="Reaction" value={form.reaction} onChange={(v) => setForm({ ...form, reaction: v })} />
            <label className="block">
              <span className="text-slate-600">Severity</span>
              <select value={form.severity || "moderate"} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded">
                {["mild", "moderate", "severe", "life_threatening"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
          </>}
          {kind === "immunisations" && <>
            <Field label="Vaccine" value={form.vaccine} onChange={(v) => setForm({ ...form, vaccine: v })} />
            <Field label="Administered on" type="date" value={form.administered_on} onChange={(v) => setForm({ ...form, administered_on: v })} />
            <Field label="Next due" type="date" value={form.next_due} onChange={(v) => setForm({ ...form, next_due: v })} />
          </>}
          {kind === "incidents" && <>
            <Field label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
            <label className="block">
              <span className="text-slate-600">Severity</span>
              <select value={form.severity || "minor"} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded">
                {["minor", "moderate", "severe", "critical"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
          </>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="btn-save-record" className="hl-btn-accent px-4 py-2 text-sm">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="text-slate-600">{label}</span>
      <input type={type} placeholder={placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded" />
    </label>
  );
}
